import React, { useState, useMemo } from 'react';
import { TableColumn } from 'tea-component';
import { formatExeclNum, formatNum, getChartDateFormatString } from '@/pages/dashboard/utils/utils';
import { useWeekDay } from '@/pages/dashboard/utils/customHook';
import { connect, getLocale, useIntl } from 'umi';
import { getExplainVal } from '@/pages/dashboard/utils/getExplainVal';
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import ChurnChartCo from '../Nightingale/component/ChurnChartCo';
import { COLOR_ARR } from '@/pages/dashboard/_config/constant';
import dateTimeFormat from '@home/utils/dbDataFormat/dateTimeFormat';

const ChurnUsersOnlineTimeDistributionCo = (props : any) => {
  const locale = getLocale();
  const { getWeekDay } = useWeekDay();
  const { formatMessage } = useIntl(); // 中英切换方法
  const isEN = locale === 'en-US';
  const { config: propsConfig, data: apiData, explainInfo, filterVal, apiDataKey } = props;
  const { timeType } = filterVal;
  const { metricsKey, title: titleEn, titleCn, toolTip, titleDes: titleDesConfig, defaultActive = 1, hideConfig, height } = propsConfig || {};
  const [isEye, setIsEye] = useState(true);

  const title = isEN ? titleEn : titleCn;
  const toolTipTitle = isEN ? toolTip?.title : toolTip?.titleCn || '';
  const toolTipMetricsName = isEN ? toolTip?.metrics : toolTip?.metricsCn || '';

  const titleDes = titleDesConfig?.map((val)=> {
    const explain = getExplainVal(explainInfo, val?.rainbowKey, getLocale(), val?.explainEn, val?.explainCn);
    const content = isEN ? val.contentEn : val.contentCn;

    if(!explain && !content){
      return null;
    }

    return {
      title: isEN ? val.title : val.titleCn,
      content: val.isRainbow ? explain : content
    };
  }).filter((val : { title: string; content: string; } | null) => val !== null);

  const data = useMemo(()=> {
    const metricsData = apiData?.[apiDataKey] || {};
    let isLoading = true;
    let isError = false;
    let isNoData = false;
    let componentData : any = [];
    if(metricsData){
      componentData = metricsKey ? metricsData?.data?.[metricsKey] : metricsData?.data || [];
      isLoading = metricsData?.loading;
      isError = metricsData?.errorMsg;
      isNoData = componentData?.length <= 0;
    }
    return {
      componentData,
      isLoading,
      isError,
      isNoData
    };
  },[apiData, apiDataKey, metricsKey]);

  const options = useMemo(() => {
    const otd_list = Array.isArray(data?.componentData) ? data.componentData : [] || [];
    if(otd_list?.length === 0){
      return;
    }
    // 图表数组
    const chart: any[] = [];
    otd_list?.forEach((cur) => {
      const { date, kv_list = [] } = cur;
      kv_list?.forEach((item) => {
        const { key, value } = item;
        const it = chart.find((el) => el.orignName === key);
        if (!it) {
          chart.push({
            orignName: key,
            name: `[${key})`,
            stack: 'bar',
            data: [
              {
                x: date,
                y: value,
              },
            ],
          });
        } else {
          it?.data?.push({
            x: date,
            y: value,
          });
        }
      });
    });
    // 表格数据设置表头->取otd_list列表的第0项数据的otd_list
    const { kv_list = [] } = otd_list?.[0] || {};
    const setMap = new Set();
    kv_list?.forEach(({ key }) => {
      setMap.add(key);
    });
    // 表头名称
    const headerNames = [...setMap];
    // 表头固定列
    const tableOption: TableColumn[] = [
      {
        key: 'date',
        align: 'left',
        fixed: 'left',
        width: 160,
        header: formatMessage({ id: 'common_date' }),
        render: (cvm) => {
          const weekDay = getWeekDay(cvm?.date);
          const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
          return <>
            <span className='table-text'>{cvm?.date}</span>
            {timeType !== 'Month' && <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>}
          </>;
        },
      },
    ];
    // 表头非固定列
    headerNames?.reduce((pre: any, key: any) => {
      pre.push({
        key,
        align: 'right',
        width: 150,
        header: key,
        render: (cvm) => <span>{cvm[key]}</span>,
      });
      return pre;
    }, tableOption);
    // 表数据
    const tableData = otd_list.reduce((outPre, outCur) => {
      const { date, kv_list = [] } = outCur;
      // row所对应的所有的column数据key->value
      const columnObj = kv_list?.reduce((pre, { key, value }) => {
        pre[key] = `${formatNum(value, false, true)  }`;
        return pre;
      }, {} as any);
      outPre.push({
        date,
        ...columnObj,
      });
      return outPre;
    }, [] as any[]);
    const downloadData = otd_list.reduce((outPre, outCur) => {
      const { date, kv_list = [] } = outCur;
      // row所对应的所有的column数据key->value
      const columnObj = kv_list?.reduce((pre, { key, value }) => {
        pre[key] = formatExeclNum(value);
        return pre;
      }, {} as any);
      outPre.push({
        date,
        ...columnObj,
      });
      return outPre;
    }, [] as any[]);
    return {
      chart,
      table: {
        option: tableOption,
        data: tableData,
        downloadData
      },
    };
  }, [data, getLocale()]);

  const formatterFunc = (params) => getEchartTooltipText({
    title: toolTipTitle,
    metricsName: toolTipMetricsName || dateTimeFormat(`${params[0].axisValue}`,'YYYY-MM-DD'),
    data: params.map(({ seriesName, value, color }) => ({
      name: seriesName,
      metricsNum: formatNum(value, false, true),
      color,
    })),
  });

  // 格式化横坐标 label
  const axisLabelFormat = (opt) => opt.xAxis?.map((item) => ({
    ...item,
    axisLabel: {
      ...item.axisLabel,
      formatter: (value) => getChartDateFormatString('Day', value),
    },
  }));

  const setOtherConfigFunc = (opt) => {
    const yTitle = {
      show: true,
      textStyle: {
        color: '#000',
        opacity: 0.65,
        fontSize: 12,
        fontWeight: 400,
      },
      textAlign: 'left',
      text: formatMessage({ id: 'engagement_churn_new_first_day_y_title' }),
      left: -4,
    };
    opt.color = COLOR_ARR;
    opt.tooltip.formatter = formatterFunc;
    opt.xAxis = axisLabelFormat(opt);
    opt.title = yTitle || {}; 
    opt.grid.y = '13%';
    return opt;
  };

  return (
    <>
      <ChurnChartCo
        options={options}
        timeType={timeType}
        title={title}
        titleDes={titleDes}
        isLoading={data.isLoading}
        isError={data.isError}
        isNoData={data.isNoData}
        defaultActive={defaultActive}
        formatterFunc={formatterFunc}
        setOtherConfigFunc={setOtherConfigFunc}
        isEye={isEye}
        setIsEye={setIsEye}
        hideConfig={hideConfig}
        height={height}
      />
    </>
  );
};

export default connect((state: any) => ({
  explainInfo: state.dashboard?.explainInfo,
}))(ChurnUsersOnlineTimeDistributionCo);